import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";

import DimensionRadarChart from "./DimensionRadarChart";
import DimensionScoreBar from "./DimensionScoreBar";
import TipCard from "./TipCard";
import ReputationHistoryTable from "./ReputationHistoryTable";
import MatchOutcomeHistoryTable from "./MatchOutcomeHistoryTable";
import PageShell from "../common/PageShell";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    paddingTop: "5rem",
    backgroundColor: "#0b0b0b",
  },
  pageFrame: {
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#0b0b0b",
    border: "1px solid rgba(255, 255, 255, 0.06)",
  },
  container: {
    width: "90%",
    maxWidth: "1400px",
    margin: "2rem auto",
    paddingBottom: "4rem",
  },
  title: {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "2rem",
    textAlign: "center",
    [theme.breakpoints.down("md")]: {
      fontSize: "2rem",
    },
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "50vh",
  },
  errorBox: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    border: "1px solid #F44336",
    borderRadius: "12px",
    padding: "2rem",
    textAlign: "center",
  },
  badgeContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
    marginBottom: "2rem",
    backgroundColor: "#1C1E20",
    borderRadius: "12px",
    border: "1px solid rgba(232, 163, 61, 0.2)",
  },
  badgeIcon: {
    fontSize: "5rem",
    marginBottom: "1rem",
  },
  tierChip: {
    fontSize: "1.5rem",
    padding: "0.75rem 2rem",
    fontWeight: 700,
    height: "auto",
  },
  overallScore: {
    fontSize: "3rem",
    fontWeight: 700,
    marginTop: "1rem",
    marginBottom: "0.5rem",
  },
  scoreLabel: {
    fontSize: "1rem",
    color: "rgba(255,255,255,0.7)",
  },
  section: {
    marginTop: "3rem",
  },
  sectionTitle: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "3rem",
  },
  dimensionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "4.5rem 2rem",
    [theme.breakpoints.down("lg")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
});

const tierConfig = {
  ELITE: { label: "ELITE", color: "#00C853" },
  HIGH: { label: "HIGH", color: "#64DD17" },
  GOOD: { label: "GOOD", color: "#FFC107" },
  FAIR: { label: "FAIR", color: "#FF9800" },
  LOW: { label: "LOW", color: "#F44336" },
};

const dimensionLabels = {
  sportsmanship: "Sportsmanship",
  integrity: "Integrity",
  communication: "Communication",
  teamPlay: "Team Play",
  reliability: "Reliability",
  reporterQuality: "Reporter Quality",
};

function ReputationDashboardPage({ classes, catalog }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reputationData, setReputationData] = useState(null);
  const [baselines, setBaselines] = useState(null);
  const [tips, setTips] = useState({});
  const [matchOutcomes, setMatchOutcomes] = useState([]);

  const fetchReputationData = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch user's detailed reputation
      const reputationResponse = await fetch(`${basePath}/api/v1/reputation/me/detailed`, {
        credentials: "include",
      });

      if (!reputationResponse.ok) {
        if (reputationResponse.status === 401 || reputationResponse.status === 403) {
          throw new Error("Please sign in to view your reputation dashboard.");
        }
        let message = "Failed to fetch reputation data";
        try {
          const errBody = await reputationResponse.json();
          if (errBody?.message) message = errBody.message;
        } catch (e) {
          // ignore JSON parse errors
        }
        throw new Error(message);
      }

      const reputationResult = await reputationResponse.json();
      setReputationData(reputationResult.profile);
      setMatchOutcomes(reputationResult.matchOutcomes || []);

      // Fetch platform baselines
      const baselinesResponse = await fetch(`${basePath}/api/v1/reputation/baseline`, {
        credentials: "include",
      });
      const baselinesResult = await baselinesResponse.json();
      setBaselines(baselinesResult.baselines);

      // Tips are computed server-side in /me/detailed (keyed by dimension).
      setTips(reputationResult.tips || {});

      setLoading(false);
    } catch (err) {
      console.error("Error fetching reputation data:", err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReputationData();
  }, [fetchReputationData]);

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loading}>
          <CircularProgress size={60} style={{ color: "#E8A33D" }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.root}>
        <div className={classes.container}>
          <Paper className={classes.errorBox}>
            <Typography variant="h6" style={{ color: "#F44336", marginBottom: "1rem" }}>
              Error Loading Reputation
            </Typography>
            <Typography style={{ color: "rgba(255,255,255,0.8)" }}>{error}</Typography>
          </Paper>
        </div>
      </div>
    );
  }

  if (!reputationData) {
    return null;
  }

  const tier = tierConfig[reputationData.trustTier] || tierConfig.GOOD;
  const baselineAverages = baselines?.baselines || {};

  return (
    <PageShell>
      <Paper className={classes.pageFrame} elevation={0}>
        <div className={classes.root}>
          <div className={classes.container}>
            <Typography className={classes.title}>Your Reputation Dashboard</Typography>

            {/* Trust Badge Section */}
            <Paper className={classes.badgeContainer}>
              <Chip
                label={tier.label}
                className={classes.tierChip}
                style={{ backgroundColor: tier.color }}
              />
              <Typography className={classes.overallScore} style={{ color: tier.color }}>
                {reputationData.overallTrustScore}
              </Typography>
              <Typography className={classes.scoreLabel}>Overall Trust Score (out of 100)</Typography>
            </Paper>

            {/* Radar Chart Section */}
            <Paper style={{ backgroundColor: "#1C1E20", padding: "1rem", marginBottom: "2rem", border: "1px solid rgba(232, 163, 61, 0.2)" }}>
              <DimensionRadarChart
                dimensions={reputationData.dimensions}
                baselines={baselineAverages}
              />
            </Paper>

            {/* Dimensional Breakdown Cards */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>Dimension Breakdown</Typography>
              <div className={classes.dimensionGrid}>
                {Object.entries(reputationData.dimensions).map(([dimKey, dimData]) => {
                  const avgScore = baselineAverages[dimKey] || 50;
                  const tip = Array.isArray(tips?.[dimKey]) ? tips[dimKey][0] : tips?.[dimKey];

                  return (
                    <DimensionScoreBar
                      key={dimKey}
                      name={dimensionLabels[dimKey] || dimKey}
                      yourScore={dimData.score}
                      average={avgScore}
                      showTip={
                        tip ? (
                          <TipCard
                            title="How to improve"
                            text={tip.text || String(tip)}
                          />
                        ) : null
                      }
                    />
                  );
                })}
              </div>
            </div>

            {/* Reputation History */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>Private Match Outcome Deltas</Typography>
              <MatchOutcomeHistoryTable rows={matchOutcomes} />
            </div>

            <div className={classes.section}>
              <ReputationHistoryTable history={reputationData.recentHistory || []} />
            </div>
          </div>
        </div>
      </Paper>
    </PageShell>
  );
}

ReputationDashboardPage.propTypes = {
  classes: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};

export default withStyles(styles)(ReputationDashboardPage);
